import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";

import serviceLoomIcon from "../../../../assets/img/serviceloom-mark.png";
import { ReactComponent as HomeIcon } from "../../../../assets/svg/home.svg";
import { ReactComponent as CustomerIcon } from "../../../../assets/svg/customer.svg";
import { ReactComponent as AdminIcon } from "../../../../assets/svg/admin.svg";
import { ReactComponent as SupportIcon } from "../../../../assets/svg/support.svg";
import { ReactComponent as AnalyticsIcon } from "../../../../assets/svg/analytics.svg";
import { ReactComponent as TimerIcon } from "../../../../assets/svg/timer.svg";
import {
  LuSettings as Settings,
  LuPanelLeft as PanelLeft,LuBuilding2,
  LuLayoutDashboard
} from "react-icons/lu";
import { GoSidebarCollapse, GoSidebarExpand } from "react-icons/go";
import { MdSupportAgent } from "react-icons/md";

const ICON_SIZE = 18;

const NAV_ROUTES = [
  {
    name: "Dashboard",
    layout: "/admin/super",
    path: "dashboard",
    icon: <LuLayoutDashboard size={18} />,
  },
  {
    name: "Customers",
    layout: "/admin/super",
    path: "clients/client-dashboard",
    icon: <LuBuilding2 size={18} />,
  },
  {
    name: "Admins",
    layout: "/admin/super",
    path: "project-admins/view",
    icon: <AdminIcon width={18} height={18} />,
  },
  {
    name: "Support Agents",
    layout: "/admin/super",
    path: "support-agents/view",
    icon: <MdSupportAgent size={22} />,
  },
  {
    name: "Reports",
    layout: "/admin/super",
    path: "reports",
    icon: <AnalyticsIcon width={18} height={18} />,
  },
  {
    name: "Support Channels",
    layout: "/admin/super",
    path: "support-channels",
    icon: <SupportIcon width={18} height={18} />,
  },
  {
    name: "SLA Management",
    layout: "/admin/super",
    path: "sla",
    icon: <TimerIcon width={18} height={18} />,
    critical: true,
  },
];

const BOTTOM_ROUTES = [
  {
    name: "Settings",
    layout: "/admin/super",
    path: "settings",
    icon: <Settings size={ICON_SIZE} />,
  },
];

const SuperAdminSidebar = ({ open = false, onClose }) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const userToggledRef = useRef(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);

      if (width >= 768 && open) {
        onClose();
      }

      if (!userToggledRef.current) {
        if (width >= 1024) {
          setCollapsed(false);
        } else if (width >= 768 && width < 1024) {
          setCollapsed(true);
        }
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [open, onClose]);

  const toggleCollapse = () => {
    if (!isMobile) {
      userToggledRef.current = true;
      setCollapsed(prev => !prev);
    }
  };

  const isRouteActive = (route) => {
    const routeKey = route.path.split("/")[0];
    return location.pathname.startsWith(`${route.layout}/${routeKey}`);
  };

  const handleLinkClick = () => {
    if (isMobile) {
      onClose();
    }
  };

  const renderNavItem = (route) => {
    const isActive = isRouteActive(route);
    return (
      <div
        key={route.name}
        className="relative"
        onMouseEnter={(e) => {
          if (collapsed) {
            const rect = e.currentTarget.getBoundingClientRect();
            setHoveredItem({
              name: route.name,
              top: rect.top + rect.height / 2
            });
          }
        }}
        onMouseLeave={() => setHoveredItem(null)}
      >
        <NavLink
          to={`${route.layout}/${route.path}`}
          onClick={handleLinkClick}
          className={`
            flex items-center transition-all duration-200 rounded-full
            font-['IBM_Plex_Sans'] text-[14px] leading-none text-left no-underline
            ${!isMobile && collapsed
              ? "w-[42px] h-[42px] justify-center mx-auto my-[6px]"
              : "w-[calc(100%-32px)] h-[42px] mx-auto my-1 pl-[15px] pr-[15px] gap-3"
            }
            ${isActive
              ? "font-medium text-[#206CEF]"
              : "font-normal text-[#B4B6C2] hover:bg-white/5 hover:text-white"
            }
          `}
          style={isActive ? {
            background: 'linear-gradient(#fff, #fff) padding-box, linear-gradient(90deg, #FF6B4A 0%, #603CFF 100%) border-box',
            border: '1px solid transparent',
          } : {
            border: '1px solid transparent'
          }}
        >
          <div className={`
            flex items-center justify-center w-[20px] h-[20px] flex-shrink-0 relative
            ${isActive ? "text-[#206CEF] nav-icon-active" : "text-[#B4B6C2] nav-icon-inactive"}
          `}>
            {route.icon}
            {isActive && (
              <svg
                className="absolute -top-[4px] -right-[4px] w-[26px] h-[26px] pointer-events-none overflow-visible z-10"
                viewBox="0 0 30 30"
                fill="none"
              >
                <path
                  d="M28.0781 16C28.3594 16 28.6406 16 28.9219 16C29.0156 15.6216 29.1 15.2431 29.175 14.8647C29.1991 14.7431 29.2222 14.6216 29.2444 14.5C29.4998 13.0308 29.3949 11.4836 28.9341 10.0462C27.2425 4.50681 21.7362 0.497254 16.1775 0.536298C15.7827 0.532747 15.3924 0.546912 15 0.578125C15 0.859375 15 1.14062 15 1.42188C15.3691 1.45316 15.7311 1.49845 16.0924 1.55777C21.1666 2.35983 25.3402 6.09196 26.7908 10.7307C27.178 11.9455 27.5012 13.1808 27.7556 14.5C27.7778 14.6216 27.8009 14.7431 27.825 14.8647C27.9 15.2431 27.9844 15.6216 28.0781 16Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </div>

          {(!collapsed || isMobile) && (
            <span className="text-sm whitespace-nowrap">
              {route.name}
            </span>
          )}

          {(!isMobile && collapsed && route.critical) && (
            <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-[#DC2626]" />
          )}
        </NavLink>

        {collapsed && hoveredItem?.name === route.name && (
          <div
            className="fixed left-[85px] z-[99999] pointer-events-none"
            style={{ top: hoveredItem.top }}
          >
            <div className="bg-gray-900 text-white text-xs font-medium py-1 px-2.5 rounded-md shadow-xl whitespace-nowrap -translate-y-1/2 border border-gray-700/50">
              {route.name}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {open && isMobile && (
        <div
          className="fixed inset-0 z-20 bg-black/50 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed md:relative h-full flex flex-col flex-shrink-0
          bg-[#2A2D3A] shadow-lg
          border-r border-b border-[#383B48] border-l-0 border-t-0
          transform transition-all duration-300 ease-in-out
          ${isMobile ? (open ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'}
          ${!isMobile && collapsed ? "w-[78px]" : "w-[210px]"}
          z-30 md:z-30
        `}
        style={{
          top: '0px',
          opacity: 1,
        }}
      >
        {/* HEADER — Gemini-style collapse button & logo hover */}
        <div className={`h-[70px] flex items-center bg-[#2A2D3A] ${!isMobile && collapsed ? "px-0 justify-center" : "justify-between pl-4 pr-2"}`}>
          {!isMobile && collapsed ? (
            <div className="w-full flex items-center justify-center">
              <div className="relative group">
                <button
                  type="button"
                  onClick={toggleCollapse}
                  className="relative flex items-center justify-center w-9 h-9 rounded-xl hover:bg-[#383B48] transition-all duration-200 cursor-pointer"
                  aria-label="Expand menu"
                >
                  <img
                    src={serviceLoomIcon}
                    alt="ServiceLoom"
                    className="h-7 w-7 object-contain group-hover:opacity-0 transition-opacity duration-200"
                  />
                  <GoSidebarCollapse
                    size={18}
                    className="absolute text-gray-300 group-hover:text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  />
                </button>

                {/* Tooltip */}
                <div className="fixed left-[85px] top-[24px] hidden group-hover:flex items-center z-[99999] pointer-events-none">
                  <div className="bg-gray-900 text-white text-xs font-medium px-2.5 py-1 rounded-md shadow-xl whitespace-nowrap border border-gray-700/50">
                    Expand menu
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2.5 min-w-0 pl-1">
                <img src={serviceLoomIcon} alt="ServiceLoom" className="h-7 w-7 object-contain flex-shrink-0" />
                <span className="text-white font-semibold text-lg">ServiceLoom</span>
              </div>
              {!isMobile && (
                <div className="relative group mr-1">
                  <button
                    type="button"
                    onClick={toggleCollapse}
                    className="p-1.5 rounded-xl text-gray-300 hover:text-white hover:bg-[#383B48] transition-all duration-200 flex-shrink-0 cursor-pointer"
                    aria-label="Collapse menu"
                  >
                    <GoSidebarExpand size={18} />
                  </button>

                  {/* Tooltip */}
                  <div className="absolute right-0 top-full mt-1.5 hidden group-hover:flex items-center z-[99999] pointer-events-none">
                    <div className="bg-gray-900 text-white text-xs font-medium px-2.5 py-1 rounded-md shadow-xl whitespace-nowrap border border-gray-700/50">
                      Collapse menu
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* NAV */}
        <nav className="flex-1 overflow-y-auto sidebar-thin-scrollbar">
          <div className="space-y-1">
            {NAV_ROUTES.map(route => renderNavItem(route))}
          </div>
        </nav>

        {/* BOTTOM NAV - Settings at the very bottom end */}
        <div className="pb-6 pt-2 mt-auto">
          <div className="space-y-1">
            {BOTTOM_ROUTES.map(route => renderNavItem(route))}
          </div>
        </div>
      </aside>

      <style>{`
        .sidebar-thin-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: transparent transparent;
        }
        .sidebar-thin-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .sidebar-thin-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-thin-scrollbar::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 10px;
        }
        .sidebar-thin-scrollbar:hover {
          scrollbar-color: rgba(255, 255, 255, 0.18) transparent;
        }
        .sidebar-thin-scrollbar:hover::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.18);
        }
        .sidebar-thin-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
        .nav-icon-inactive .dashboard-home-arc {
          display: none;
        }
        .nav-icon-active .dashboard-home-arc {
          display: block;
        }
      `}</style>
    </>
  );
};

export default SuperAdminSidebar; 